import { isValidElement, useEffect, useState } from 'react';
import { DialogContentText, Drawer as MuiDrawer, useMediaQuery } from '@mui/material';
import debounce from 'lodash.debounce';
import { slideOutDefaultStyle } from './slideOutDefaultStyle';
import { mergeSxStyle } from '../../common/mergeSxStyle';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { SlideOutTitle } from './SlideOutTitle';
import { Box } from '../../Box';
import { ActionButtons } from '../../ActionButtons';
import { clsx } from 'clsx';
import { idBuilder } from '../../internal';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const XS_BREAKPOINT = 479;
export const resolveContent = (content, customIdentifiers) => {
  if (typeof content === 'string') {
    return /*#__PURE__*/_jsx(DialogContentText, {
      id: customIdentifiers['contentText']?.id,
      "data-testid": customIdentifiers['contentText']?.dataTestId,
      children: content
    });
  }
  return content;
};
/**
 * Used to determine which layout to use.  If panelWidth > breakpoint use layout property value.
 * @param xsLayout
 * @param layout
 * @param useXS
 */
const resolveLayout = (xsLayout, layout, useXS) => {
  if (useXS && xsLayout) {
    return xsLayout;
  }
  return layout ? layout : 'rightAligned';
};

/**
 * used to determine which type of footer do display.  If a component is provided then display the component
 * @param footer
 * @param hasOverflow
 * @param useXS
 * @param dataTestId
 * @param id
 * @param customIdentifiers
 */
export const resolveFooter = (footer, hasOverflow, useXS, dataTestId, id, customIdentifiers) => {
  if (!footer) {
    return null;
  }
  if ( /*#__PURE__*/isValidElement(footer.footerComponent)) {
    return footer.footerComponent;
  }
  if (!footer.primary) {
    return null;
  }

  // the layout can change based on the size of the SlideOut
  // the useXS is triggered based on the width of the SlideOut
  const effectiveLayout = resolveLayout(footer?.xsLayout, footer?.layout, useXS);
  return /*#__PURE__*/_jsx(ActionButtons, {
    size: 'medium',
    primary: footer.primary,
    options: footer.options,
    overflow: hasOverflow,
    layout: effectiveLayout,
    id: id,
    "data-testid": dataTestId,
    customIdentifiers: customIdentifiers
  });
};

/** A panel that moves in from either side of the screen  */
export const SlideOut = _ref => {
  let {
    position = 'right',
    show,
    children,
    className,
    onHide,
    panelWidth = 280,
    header,
    footer,
    sx,
    id,
    bottomOffset,
    topOffset,
    customIdentifiers = {},
    disableEnforceFocus = false,
    ...rest
  } = _ref;
  const theme = useInterstateTheme();
  const [ref, setRef] = useState();
  const [hasOverflow, setHasOverflow] = useState(false);
  const [effectiveWidth, setEffectiveWidth] = useState(panelWidth);
  useEffect(() => {
    const handleResize = debounce(() => {
      if (ref) {
        const isOverflowing = ref.scrollHeight > ref.clientHeight;
        setHasOverflow(isOverflowing);
      }
    }, 200);

    // Check for overflow when the component is mounted
    handleResize();
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [ref]);
  useEffect(() => {
    if (ref) {
      const isOverflowing = ref.scrollHeight > ref.clientHeight;
      setHasOverflow(isOverflowing);
    }
  }, [panelWidth]);
  // Check to see if the width of the panel exceeds the media breakpoint.  If it does then
  // set the panel width to 100%
  const matchesBreakpoint = useMediaQuery(`(max-width:${XS_BREAKPOINT}px)`);
  useEffect(() => {
    if (matchesBreakpoint && panelWidth) {
      setEffectiveWidth('100%');
    } else {
      setEffectiveWidth(panelWidth);
    }
  }, [panelWidth, matchesBreakpoint]);
  const drawerStyle = mergeSxStyle(slideOutDefaultStyle(theme, effectiveWidth, hasOverflow, bottomOffset, topOffset), sx);
  const effectiveFooter = resolveFooter(footer, hasOverflow, matchesBreakpoint, rest['data-testid'], id, customIdentifiers);
  return /*#__PURE__*/_jsxs(MuiDrawer, {
    anchor: position,
    className: clsx([className, 'ids-SlideOut-root']),
    open: show,
    onClose: onHide,
    variant: "temporary",
    sx: drawerStyle,
    "data-testid": customIdentifiers['root']?.dataTestId || idBuilder('ids-SlideOut-root', rest['data-testid']),
    id: customIdentifiers['root']?.id || idBuilder('ids-SlideOut-root', id),
    disableEnforceFocus: disableEnforceFocus,
    children: [/*#__PURE__*/_jsx(SlideOutTitle, {
      onClose: onHide,
      id: id,
      displayCloseButton: true,
      hasOverflow: hasOverflow,
      "data-testid": rest['data-testid'],
      customIdentifiers: customIdentifiers,
      children: header && header
    }), /*#__PURE__*/_jsxs(Box, {
      padding: hasOverflow ? '0 24px 0 24px' : '0 24px 32px 24px',
      ref: setRef,
      height: '100%',
      sx: {
        overflowY: 'auto'
      },
      className: 'ids-SlideOut-content',
      id: customIdentifiers['contentContainer']?.id || idBuilder('ids-SlideOut-content', id),
      "data-testid": customIdentifiers['contentContainer']?.dataTestId || idBuilder('ids-SlideOut-content', rest['data-testid']),
      children: [resolveContent(children, customIdentifiers), !hasOverflow && effectiveFooter && /*#__PURE__*/_jsx(Box, {
        padding: '20px 0 0 0',
        id: customIdentifiers['footer']?.id || idBuilder('ids-SlideOut-footer', id),
        "data-testid": customIdentifiers['footer']?.dataTestId || idBuilder('ids-SlideOut-footer', rest['data-testid']),
        children: effectiveFooter
      })]
    }), hasOverflow && effectiveFooter && /*#__PURE__*/_jsx(Box, {
      position: 'absolute',
      bottom: '0',
      right: '0',
      width: '100%',
      className: 'ids-SlideOut-overflow-footer',
      id: customIdentifiers['footer']?.id || idBuilder('ids-SlideOut-footer', id),
      "data-testid": customIdentifiers['footer']?.dataTestId || idBuilder('ids-SlideOut-footer', rest['data-testid']),
      children: effectiveFooter
    })]
  });
};